import { inject, Injectable, untracked } from "@angular/core";
import { PeoState, PeoStoreService } from "./peo-store.service";
import { EntityFacadeService } from "../../../../shared/store/entity-store/entity-facade.service";
import { EntityMetadata } from "../../../../shared/store/store-types.model";
import { PeoApiService } from "./peo-api.service";
import { ProcessEtapeOpe } from "../../models/peo.model";
import {
  Untrack,
  UpdateStatus,
} from "../../../../shared/store/utils/store-utils.service";
import { ChecklistFacadeService } from "../../../list/services/store/checklist-facade.service";
import { firstValueFrom } from "rxjs";
import { ControleFacadeService } from "../../../controle/services/store/controle-facade.service";

@Injectable({ providedIn: "root" })
export class PeoFacadeService extends EntityFacadeService<
  ProcessEtapeOpe,
  PeoApiService,
  PeoState,
  EntityMetadata,
  PeoStoreService
> {
  checklistFacadeService = inject(ChecklistFacadeService);
  controlesFacadeService = inject(ControleFacadeService);

  constructor() {
    const api = inject(PeoApiService);
    const store = inject(PeoStoreService);
    super(api, store);
    this.store.initStatus(PeoFacadeService);
  }

  @Untrack
  @UpdateStatus
  async startPeo(id: string) {
    const peo = await this.api.startPeo(id);
    this.store.upsertOne(peo);
    return peo;
  }

  @Untrack
  @UpdateStatus
  async endPeo(id: string) {
    const peo = await this.api.endPeo(id);
    this.store.upsertOne(peo);
    return peo;
  }

  @Untrack
  @UpdateStatus
  async endPeoForChecklist(id: string, checklistId: string) {
    const res = await this.api.endPeoForChecklist(id, checklistId);
    this.store.upsertOne(res.peo);
    this.checklistFacadeService.store.upsertOne(res.checklist);
    return res.peo;
  }

  /**
   * Étape de checklist d'OF déclarée NON APPLICABLE ou rendue applicable (05/10/2026) : l'étape et
   * la checklist recalculée (état, version) sont rangées dans le store, comme pour une fin d'étape.
   */
  @Untrack
  @UpdateStatus
  async setNonApplicableForChecklist(id: string, checklistId: string, nonApplicable: boolean) {
    const res = await this.api.setNonApplicableForChecklist(id, checklistId, nonApplicable);
    this.store.upsertOne(res.peo);
    this.checklistFacadeService.store.upsertOne(res.checklist);
    return res.peo;
  }

  /**
   * Étape de procédé d'un POSTE déclarée non applicable / rendue applicable (06/10/2026) : la PEO
   * rendue par le serveur est rangée dans le store (bandeau, état de la sous-étape, validation).
   */
  @Untrack
  @UpdateStatus
  async setNonApplicable(id: string, nonApplicable: boolean, commentaire?: string | null) {
    const peo = await this.api.setNonApplicable(id, nonApplicable, commentaire);
    if (peo) this.store.upsertOne(peo);
    return peo;
  }

  @Untrack
  @UpdateStatus
  async resetEndPeo(id: string) {
    const peo = await this.api.resetEndPeo(id);
    this.store.upsertOne(peo);
    return peo;
  }

  /**
   * Recharge les PEO d'UN poste et retire du store celles de CE poste que le
   * serveur ne rend plus, puis ajoute / met à jour les autres.
   *
   * ⚠️ `loadAll({ posteId })` ne fait qu'ajouter (`addMany`, sans purge). Or une
   * propagation de spécification SUPPRIME des PEO en base : exclure une étape
   * optionnelle d'un OF en production efface sa PEO, la réintégrer en crée une
   * neuve. L'ancienne restait dans le store, `initProcess` du poste-starter la
   * trouvait la première par `processEtapeUUID`, la carte saisissait sur son
   * POD disparu, `endPeo` ne mettait à jour aucune ligne, et la validation
   * finale déclarait l'étape non faite.
   *
   * Le retrait ne vise QUE le poste rechargé : les PEO d'un autre poste, d'une
   * checklist (`posteId` nul, `posteRefId` posé) ou d'une opération d'essai
   * appartiennent à d'autres écrans et ne sont pas dans la réponse.
   */
  @Untrack
  @UpdateStatus
  async loadForPoste(posteId: string): Promise<void> {
    if (!posteId) return;
    this.store.incrementLoading();
    try {
      const peos = (await firstValueFrom(this.api.loadAll({ posteId }))) ?? [];
      const rendues = new Set(peos.map((p) => p.id));
      for (const peo of untracked(() => this.getAllEntities()())) {
        if (peo?.posteId === posteId && !rendues.has(peo.id)) {
          this.store.removeOne(peo.id);
        }
      }
      this.store.addMany(peos);
      this.store.decrementLoading();
    } catch (e) {
      this.store.decrementLoading();
      this.store.patchState({ error: e });
      throw e;
    }
  }

  @Untrack
  @UpdateStatus
  async loadAllForChecklistAndSetPods(checklistId: string) {
    const peos = await firstValueFrom(
      this.api.loadAllEagerForChecklist(checklistId),
    );
    for (let peo of peos) {
      this.store.upsertOne(peo);
      if (peo.pod?.controles?.length) {
        for (let c of peo.pod.controles) {
          this.controlesFacadeService.store.upsertOne(c);
        }
      }
    }
  }
}
